import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API } from "../lib/api";
import Layout from "../components/Layout";
import AuthImage from "../components/AuthImage";
import OnlyFansAdStripe from "../components/OnlyFansAdStripe";
import "../styles/coaching.css";
import { Trophy, BookText } from "lucide-react";
import { renderContestTitle } from "../lib/contestTitle";

function PreviewThumb({ p, contestType }) {
  const media = p.media;
  if (contestType === "novelle") {
    // Cover om finnes, ellers tekst-teaser
    if (media?.cover_url) {
      return (
        <img
          src={media.cover_url}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      );
    }
    return (
      <div className="w-full h-full flex flex-col p-2 bg-[var(--noir-2)]">
        <BookText className="h-3 w-3 text-[var(--gold)] mb-1 shrink-0" />
        <p className="text-[10px] leading-tight text-[var(--cream-soft)] line-clamp-5">
          {media?.teaser || media?.title || "Novelle"}
        </p>
      </div>
    );
  }
  // image / video
  // Direkte URL fra API (media.url / thumbnail_url) er tillatt hvis satt.
  // Ellers henter vi signert URL via AuthImage.
  const directUrl = media && (media.url || media.thumbnail_url);
  if (directUrl) {
    return (
      <img
        src={directUrl}
        alt=""
        className="w-full h-full object-cover"
        loading="lazy"
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  }
  if (p.ref_id) {
    return (
      <AuthImage
        imageId={p.ref_id}
        alt=""
        className="w-full h-full object-cover"
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center bg-[var(--noir-2)]">
      <Trophy className="h-4 w-4 text-[var(--gold)] opacity-30" />
    </div>
  );
}

function ContestCard({ c }) {
  const { t } = useTranslation();
  const PHASE_LABELS = {
    upload:         { label: t("contests.badge_open"),    color: "var(--magenta)" },
    voting:         { label: t("contests.badge_voting"),  color: "var(--gold)" },
    closed_pending: { label: t("common.ending_soon", "Avsluttes snart"), color: "var(--cream-soft)" },
    closed:         { label: t("common.ended", "Avsluttet"), color: "var(--cream-faint)" },
  };
  const TYPE_LABELS = {
    image:   t("contests.type_image"),
    video:   t("contests.type_video"),
    novelle: t("contests.type_story"),
    creator: t("contests.type_creator"),
  };
  const ph = PHASE_LABELS[c.phase] || {};
  const previews = (c.preview_entries || []).slice(0, 6);
  const hasPreviews = previews.length > 0;

  return (
    <Link
      to={`/konkurranser/${c.id}`}
      data-testid={`contest-card-${c.id}`}
      className="panel-card block transition hover:border-[var(--magenta)]/50 group"
    >
      {/* Thumbnail-grid – 6 random bidrag, roterer hver time */}
      <div className="relative bg-[var(--noir-elev)]">
        {hasPreviews ? (
          <div className="grid grid-cols-3 grid-rows-2 gap-px bg-black/40 aspect-[3/2]">
            {previews.map((p) => (
              <div key={p.id} className="relative overflow-hidden bg-[var(--noir-2)]">
                <PreviewThumb p={p} contestType={c.type} />
              </div>
            ))}
            {/* Fyll resten av rutenettet med trofé-placeholders hvis < 6 */}
            {Array.from({ length: Math.max(0, 6 - previews.length) }).map((_, i) => (
              <div key={`empty-${i}`} className="bg-[var(--noir-2)] flex items-center justify-center">
                <Trophy className="h-4 w-4 text-[var(--gold)] opacity-20" />
              </div>
            ))}
          </div>
        ) : (
          <div className="aspect-[3/2] flex items-center justify-center">
            <Trophy className="h-12 w-12 text-[var(--gold)] opacity-30" />
          </div>
        )}
        <div
          className="absolute top-2 left-2 font-meta px-2 py-0.5 bg-black/70 backdrop-blur-sm border"
          style={{ borderColor: ph.color, color: ph.color }}
        >
          {ph.label || c.phase}
        </div>
      </div>

      <div className="p-4">
        <div className="font-meta text-[var(--cream-soft)] mb-1">{TYPE_LABELS[c.type] || c.type}</div>
        <h3 className="font-fraunces italic text-xl text-[var(--cream)] group-hover:text-[var(--magenta)] transition">
          {renderContestTitle(c, t)}
        </h3>
        <div className="flex items-center justify-between mt-3 text-xs text-[var(--cream-faint)]">
          <span>{t(c.entry_count === 1 ? "contests.entries_count_one" : "contests.entries_count_other", { count: c.entry_count })}</span>
          {c.prize_amount > 0 && <span className="text-[var(--gold)]">{c.prize_amount.toLocaleString("nb-NO").replace(/\u00A0/g, " ")} {t("contests.chips")}</span>}
        </div>
        {hasPreviews && c.entry_count > previews.length && (
          <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-meta text-[var(--magenta)] group-hover:underline">
            {t("contests.see_all_entries", { n: c.entry_count })}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function Konkurranser() {
  const { t } = useTranslation();
  const [contests, setContests] = useState([]);
  const [tab, setTab] = useState("active");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const state = tab === "closed" ? "closed" : tab === "all" ? "all" : "active";
    api.get(`/contests?state=${state}`).then((r) => {
      let rows = r.data?.results || [];
      if (tab === "active") rows = rows.filter((c) => c.phase !== "closed");
      setContests(rows);
    }).finally(() => setLoading(false));
  }, [tab]);

  return (
    <Layout>
      {/* Kompakt header-rad: tittel + Hall of Fame side ved side */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <h1 className="font-fraunces italic text-4xl sm:text-5xl text-[var(--cream)] leading-tight">
          {t("contests.page_title")}
        </h1>
        <Link
          to="/konkurranser/vinnere"
          data-testid="hall-of-fame-btn"
          className="px-4 py-2 border border-[var(--gold)]/40 text-[var(--gold)] font-meta inline-flex items-center gap-2 hover:bg-[var(--gold)]/10 shrink-0"
        >
          <Trophy className="h-4 w-4" /> {t("contests.hall_of_fame")}
        </Link>
      </div>
      <p className="text-[var(--cream-soft)] text-sm max-w-2xl mb-2">
        {t("contests.page_intro")}
      </p>
      <p className="text-[var(--cream-faint)] text-xs font-meta mb-6" data-testid="vote-rule-info">
        {t("contests.vote_rule_global", { n: 1 }).toUpperCase()}
      </p>

      <div className="flex gap-2 mb-8 border-b border-white/10">
        {[
          ["active", t("contests.tab_active")],
          ["closed", t("contests.tab_ended")],
          ["all",    t("contests.tab_all")],
        ].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setTab(v)}
            data-testid={`tab-${v}`}
            className={`px-4 py-2 font-meta border-b-2 transition ${tab === v ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-transparent text-[var(--cream-soft)] hover:text-[var(--cream)]"}`}
          >
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="panel p-12 text-center text-[var(--cream-soft)]">{t("common.loading")}</div>
      ) : contests.length === 0 ? (
        <div className="panel-tight p-12 text-center">
          <Trophy className="h-12 w-12 text-[var(--gold)] mx-auto mb-4 opacity-40" />
          <p className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">Ingen konkurranser her enda.</p>
          <p className="text-[var(--cream-soft)] text-sm">Vi setter snart i gang den første kåringen.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {contests.map((c, idx) => (
            <React.Fragment key={c.id}>
              <ContestCard c={c} />
              {idx === 4 && contests.length > 5 && <OnlyFansAdStripe variant="contest-card" />}
            </React.Fragment>
          ))}
        </div>
      )}
    </Layout>
  );
}
